import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import {
  Gamepad2,
  Users,
  Ticket,
  Send,
  Sparkles,
  Trophy,
  Clock,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n);
const pct = (n) => `${(n || 0).toFixed(1)}%`;

const KPI = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="p-5 shadow-[var(--shadow-card)]" data-testid="kpi-card">
    <div className="flex items-start justify-between">
      <div>
        <p
          className="text-xs uppercase tracking-wider text-muted-foreground"
          data-testid="kpi-card-label"
        >
          {label}
        </p>
        <p
          className="font-heading text-3xl font-semibold mt-1 tabular-nums"
          data-testid={testid || "kpi-card-value"}
        >
          {value}
        </p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

const FunnelBar = ({ label, value, max, color, hint }) => {
  const width = max > 0 ? Math.max(4, (value / max) * 100) : 4;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {fmt(value)} {hint ? <span className="ml-1 text-xs">({hint})</span> : null}
        </span>
      </div>
      <div className="mt-1.5 h-3 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${width}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

export default function OverviewPage() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.overview(30), api.listLeads()])
      .then(([o, l]) => {
        setData(o.data);
        setRecent((l.data || []).slice(0, 8));
      })
      .finally(() => setLoading(false));
  }, []);

  const funnel = data?.funnel || { leads: 0, coupons: 0, redeemed: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Overview</h1>
          <p className="text-muted-foreground text-sm">Your performance at a glance.</p>
        </div>
        <Link to="/vendor/campaigns/new">
          <Button data-testid="create-campaign-cta">
            <Sparkles className="mr-2 h-4 w-4" /> Create campaign
          </Button>
        </Link>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading || !data ? (
          [0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5">
              <Skeleton className="h-16 w-full" />
            </Card>
          ))
        ) : (
          <>
            <KPI
              icon={Gamepad2}
              label="Total Plays"
              value={fmt(data.total_leads)}
              /* Only show the game-type breakdown when the vendor
                 actually has BOTH mechanics running. A vendor with
                 only Spin Wheel campaigns doesn't need a pointless
                 "Scratches: 0" line, and vice versa. */
              sub={(() => {
                const g = data.plays_by_game_type || {};
                const s = g.spin_wheel || 0;
                const c = g.scratch_card || 0;
                if (s > 0 && c > 0) return `Spins: ${fmt(s)} · Scratches: ${fmt(c)}`;
                return undefined;
              })()}
              testid="kpi-total-plays"
            />
            <KPI
              icon={Users}
              label="Leads (30d)"
              value={fmt(data.leads_period)}
              testid="kpi-leads-period"
            />
            <KPI
              icon={Trophy}
              label="Coupons Redeemed"
              value={fmt(data.redeemed_coupons)}
              sub={pct(funnel.coupon_to_redeem_pct) + " of issued"}
              testid="kpi-redeemed"
            />
            <KPI
              icon={Send}
              label="Messages Sent"
              value={fmt(data.messages_sent)}
              testid="kpi-messages"
            />
          </>
        )}
      </div>

      {/* Funnel + Avg redemption time */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="p-5 lg:col-span-7 shadow-[var(--shadow-card)]" data-testid="overview-funnel-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Conversion funnel</h2>
          </div>
          {loading || !data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="space-y-4">
              <FunnelBar label="Leads (plays)" value={funnel.leads} max={funnel.leads} color="hsl(var(--primary))" />
              <FunnelBar
                label="Coupons issued"
                value={funnel.coupons}
                max={funnel.leads || 1}
                color="hsl(var(--chart-2))"
                hint={pct(funnel.lead_to_coupon_pct)}
              />
              <FunnelBar
                label="Coupons redeemed"
                value={funnel.redeemed}
                max={funnel.leads || 1}
                color="hsl(var(--chart-4))"
                hint={pct(funnel.lead_to_redeem_pct) + " of leads"}
              />
            </div>
          )}
        </Card>
        <Card className="p-5 lg:col-span-5 shadow-[var(--shadow-card)]" data-testid="overview-redemption-card">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Redemption pace</h2>
          </div>
          {loading || !data ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div>
              <p
                className="font-heading text-4xl font-semibold tabular-nums"
                data-testid="overview-avg-redemption"
              >
                {data.avg_redemption_hours == null
                  ? "—"
                  : data.avg_redemption_hours < 24
                    ? `${data.avg_redemption_hours}h`
                    : `${(data.avg_redemption_hours / 24).toFixed(1)}d`}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Average time from win to redemption.
              </p>
              <div className="mt-4 text-sm flex items-center gap-2">
                <Ticket className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Total coupons issued: <span className="tabular-nums font-semibold text-foreground">{fmt(data.total_coupons)}</span>
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Line chart */}
      <Card className="p-5 shadow-[var(--shadow-card)]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg font-semibold">Spins over time</h2>
            <p className="text-xs text-muted-foreground">Last 30 days</p>
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          {loading || !data ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <ResponsiveContainer>
              <LineChart data={data.series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  tickFormatter={(v) => v?.slice(5)}
                />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                  }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Line type="monotone" dataKey="spins" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Top campaigns */}
      <Card className="p-5 shadow-[var(--shadow-card)]" data-testid="overview-top-campaigns-card">
        <h2 className="font-heading text-lg font-semibold mb-3">Top campaigns (last 30 days)</h2>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : !data?.top_campaigns?.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No spins recorded yet. Launch a campaign to start collecting data.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {data.top_campaigns.map((c, i) => (
              <div
                key={c.campaign_id}
                className="py-3 flex items-center gap-3"
                data-testid="overview-top-campaign-row"
              >
                <div className="h-8 w-8 rounded-lg bg-accent text-accent-foreground grid place-items-center text-sm font-semibold">
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{fmt(c.spins)} spins</div>
                </div>
                <Link to={`/vendor/campaigns/${c.campaign_id}/analytics`}>
                  <Button variant="ghost" size="sm">
                    Analytics <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 shadow-[var(--shadow-card)]">
        <h2 className="font-heading text-lg font-semibold mb-3">Recent leads</h2>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No leads yet. Launch a campaign to start collecting!</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Phone</th>
                  <th className="py-2 pr-3">Prize</th>
                  <th className="py-2 pr-3">When</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((l) => (
                  <tr key={l.id} className="border-b last:border-0 border-border" data-testid="recent-lead-row">
                    <td className="py-2 pr-3 font-medium">{l.name}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{l.phone}</td>
                    <td className="py-2 pr-3">{l.won_gift_name || l.won_label}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {l.spun_at ? new Date(l.spun_at).toLocaleString() : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
